import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cx } from './classNames';

// MUI's text Button in the app theme (MuiButton in
// styles/msiMuiComponentOverrides.js): the accent text with the
// selected-blue fill on hover, 7px radius, weight 750, no text transform,
// 2px focus outline; disabled takes the disabled fill and text. Only the
// font family is inherited (not the `font` shorthand of
// controlResetClassName, which would override the size and weight set here).
const BASE_CLASS_NAME = cx(
    'm-0 appearance-none normal-case [font-family:inherit]',
    'inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-[7px] font-[750]',
    'tracking-normal no-underline transition-colors duration-100 focus:outline-none focus-visible:outline',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ui-focus',
    'disabled:cursor-default disabled:bg-ui-disabled-fill disabled:text-ui-disabled-text',
    'border-0 bg-transparent text-ui-link hover:bg-ui-selected',
);
const SIZE_CLASS_NAMES = Object.freeze({
    // The app's control: 36px tall (30px compact), 11.5px text (10.5px).
    md: 'min-h-9 px-3 py-[7px] text-[11.5px] leading-[1.1] [[data-compact]_&]:min-h-[30px] '
        + '[[data-compact]_&]:py-[5px] [[data-compact]_&]:text-[10.5px]',
    // An inline action inside dense content (table cells, hints): no padding
    // or minimum height, the surrounding text size.
    sm: 'min-h-0 p-0 [font-size:inherit] leading-[1.2]',
});

/**
 * A text button at the control size (`md`) or the inline size (`sm`).
 * Other props go to the <button>.
 */
const Button = forwardRef(function Button({
    size = 'md',
    type = 'button',
    className,
    children,
    ...buttonProps
}, ref) {
    return (
        <button
            ref={ref}
            type={type}
            className={cx(BASE_CLASS_NAME, SIZE_CLASS_NAMES[size], className)}
            {...buttonProps}
        >
            {children}
        </button>
    );
});

Button.propTypes = {
    size: PropTypes.oneOf(['md', 'sm']),
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    className: PropTypes.string,
    children: PropTypes.node,
};

export default Button;
